import React from "react";
import type { ReactElement } from "react";
import { Skeleton, cn } from "@ndd/ui";
import type { FunctionalFormLink } from "../../lib/metrics-display";
import { formatLocalDate, weeksSince } from "../../../../shared/local-date";
import type {
  FormEmploymentLoad,
  FormMeetingCadence,
  FormReviewStatus,
} from "../../../../shared/form-types";
import type { FormProfileData } from "./forms-profile-card";
import {
  THEMED_BAND,
  THEMED_CARD,
  THEMED_CARD_TITLE,
  THEMED_EYEBROW,
} from "../../lib/always-look";

// Visual grammar copied from forms-profile-card.tsx, where these constants are
// module-private; promoting them to a shared module is outside NO-558.
// Under the always theme (NO-792) the card is white with a hairline, its band
// card-white, its title in display type and its labels mono eyebrows.
const CARD = cn(
  "overflow-hidden rounded-[14px] border border-border bg-card shadow-[0_1px_2px_rgba(40,3,15,0.05)]",
  THEMED_CARD
);
const CARD_HEAD = cn(
  "flex min-h-[48px] items-center justify-between gap-3 border-b border-border bg-muted px-4 py-2",
  THEMED_BAND
);
const CARD_TITLE = cn(
  "font-sans text-label font-extrabold uppercase tracking-[0.06em] text-foreground",
  THEMED_CARD_TITLE
);
const LABEL_BASE = cn(
  "text-caption font-semibold uppercase tracking-wider text-muted-foreground",
  THEMED_EYEBROW
);

const CADENCE_LABELS: Record<FormMeetingCadence, string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  as_needed: "As needed",
};
const EMPLOYMENT_LABELS: Record<FormEmploymentLoad, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
};
const REVIEW_STATUS_LABELS: Record<FormReviewStatus, string> = {
  not_scheduled: "Not scheduled",
  scheduled: "Scheduled",
  completed: "Completed",
};

const MISSING = "—";

function tenure(startDate: string): string {
  const weeks = weeksSince(startDate);
  if (weeks < 8) return `${weeks} week${weeks === 1 ? "" : "s"}`;

  const months = Math.floor(weeks / 4.345);

  return `${months} month${months === 1 ? "" : "s"}`;
}

function hours(value: number | null): string {
  return value === null ? MISSING : `${value.toFixed(1)} hrs/wk`;
}

function Field({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string | null;
}): ReactElement {
  // Type weights follow the approved profile card: a medium 13.5px value with
  // a regular 12px muted detail, never a bold value or a same-size detail.
  return (
    <div className="flex flex-col gap-[2px]">
      <dt className={LABEL_BASE}>{label}</dt>
      <dd className="flex items-baseline gap-1 leading-5">
        <span className="text-body-lg font-medium text-foreground">{value}</span>
        {detail ? (
          <span className="text-label font-normal text-muted-foreground">
            · {detail}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

function ProfileFields({ profile }: { profile: FormProfileData }): ReactElement {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 px-4 py-4 md:grid-cols-4">
      <Field
        label="Start date"
        value={
          profile.startDate
            ? formatLocalDate(profile.startDate, "shortWithYear")
            : MISSING
        }
        detail={profile.startDate ? tenure(profile.startDate) : null}
      />
      <Field
        label="90-day review"
        value={
          profile.ninetyDayReviewStatus
            ? REVIEW_STATUS_LABELS[profile.ninetyDayReviewStatus]
            : MISSING
        }
        detail={
          profile.ninetyDayReviewDate
            ? formatLocalDate(profile.ninetyDayReviewDate)
            : null
        }
      />
      <Field
        label="Employment"
        value={
          profile.employmentLoad ? EMPLOYMENT_LABELS[profile.employmentLoad] : MISSING
        }
      />
      <Field
        label="Meeting cadence"
        value={
          profile.meetingCadence ? CADENCE_LABELS[profile.meetingCadence] : MISSING
        }
      />
      <Field label="Contracted minimum" value={hours(profile.contractMinimumHours)} />
      <Field label="Coaching target" value={hours(profile.currentTargetHours)} />
    </dl>
  );
}

const SETUP_LINK_TEXT = "Functional Forms";

/**
 * Read-only contract profile for the Snapshot. Renders only the contract facts
 * the forms profile carries; `profileNotes` and `socialNotes` never appear here.
 * A missing profile's hint links to profile setup through `setupLink`, which the
 * route supplies so this card stays router-free (NO-669).
 */
export function SnapshotProfileCard({
  profile,
  setupLink,
}: {
  profile: FormProfileData | null;
  setupLink?: FunctionalFormLink;
}): ReactElement {
  return (
    <section
      className={CARD}
      aria-label="Contract profile"
      data-testid="snapshot-profile"
    >
      <div className={CARD_HEAD}>
        <h2 className={CARD_TITLE}>Profile</h2>
      </div>
      {profile ? (
        <ProfileFields profile={profile} />
      ) : (
        <p className="px-4 py-4 text-body text-muted-strong">
          No contract profile yet. Add one under{" "}
          {setupLink ? setupLink(SETUP_LINK_TEXT) : SETUP_LINK_TEXT}.
        </p>
      )}
    </section>
  );
}

/** Same card footprint while the profile query is pending; never the empty hint. */
export function SnapshotProfileCardSkeleton(): ReactElement {
  return (
    <section
      className={CARD}
      aria-hidden="true"
      data-testid="snapshot-profile-skeleton"
    >
      <div className={CARD_HEAD}>
        <h2 className={CARD_TITLE}>Profile</h2>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-4 py-4 md:grid-cols-4">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={`profile-field-${index}`} className="flex flex-col gap-[6px]">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-28" />
          </div>
        ))}
      </div>
    </section>
  );
}
